import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../api/config';
import type { License, Activation } from '../types';
import {
  Card,
  CardHeader,
  CardContent,
  Badge,
  PageHeader,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  EmptyState,
  Skeleton,
  Alert,
} from '@license-management/ui';
import { ArrowLeft, Monitor, AlertCircle } from 'lucide-react';

interface LicenseDetail extends License {
  activations: Activation[];
  modules: string[];
}

// Format module name by removing mod_ prefix
const formatModuleName = (moduleId: string): string => {
  return moduleId.replace(/^mod_/i, '');
};

const getStatusVariant = (status: string) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'expired':
      return 'warning';
    case 'revoked':
    case 'inactive':
      return 'error';
    default:
      return 'default';
  }
};

interface DetailRowProps {
  label: string;
  children: React.ReactNode;
  alternate?: boolean;
}

function DetailRow({ label, children, alternate }: DetailRowProps) {
  return (
    <div
      className={`px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ${
        alternate ? 'bg-gray-50/50' : ''
      }`}
    >
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
        {children}
      </dd>
    </div>
  );
}

function LicenseDetailPage() {
  const { id } = useParams<{ id: string }>();

  const {
    data: license,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['license', id],
    queryFn: () => apiClient.get<LicenseDetail>(API_ENDPOINTS.license(id!)),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div>
        <div className="mb-6">
          <Skeleton className="h-4 w-32" />
        </div>
        <Card className="mb-6">
          <CardHeader title="" />
          <CardContent className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-48" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !license) {
    return (
      <Alert variant="error" icon={<AlertCircle className="h-5 w-5" />}>
        Failed to load license details. Please try again later.
      </Alert>
    );
  }

  return (
    <div>
      <PageHeader
        title={license.productName}
        description="License details and activation status"
        backLink={
          <Link
            to="/licenses"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Licenses
          </Link>
        }
        actions={
          <Badge variant={getStatusVariant(license.status)} size="lg">
            {license.status}
          </Badge>
        }
      />

      {/* License Details */}
      <Card className="mb-6">
        <CardContent className="p-0">
          <dl className="divide-y divide-gray-100">
            <DetailRow label="License Key" alternate>
              <code className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                {license.licenseKey}
              </code>
            </DetailRow>
            <DetailRow label="License Type">
              <span className="capitalize">{license.licenseType}</span>
            </DetailRow>
            <DetailRow label="Activations" alternate>
              <span>
                {license.currentActivations} of {license.maxActivations} used
              </span>
              <div className="mt-2 w-full max-w-xs bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all"
                  style={{
                    width: `${Math.min(
                      (license.currentActivations / license.maxActivations) * 100,
                      100
                    )}%`,
                  }}
                />
              </div>
            </DetailRow>
            <DetailRow label="Expires">
              {license.expiresAt
                ? new Date(license.expiresAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'Never (Perpetual)'}
            </DetailRow>
            {license.modules && license.modules.length > 0 && (
              <DetailRow label="Modules" alternate>
                <div className="flex flex-wrap gap-2">
                  {license.modules.map((module) => (
                    <Badge key={module} variant="primary">
                      {formatModuleName(module)}
                    </Badge>
                  ))}
                </div>
              </DetailRow>
            )}
            <DetailRow label="Created">
              {new Date(license.createdAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </DetailRow>
          </dl>
        </CardContent>
      </Card>

      {/* Activations */}
      <Card>
        <CardHeader
          title="Activated Devices"
          description="Machines where this license is currently activated"
        />
        <CardContent className="p-0">
          {license.activations.length === 0 ? (
            <EmptyState
              icon={<Monitor className="h-12 w-12" />}
              title="No activations yet"
              description="This license hasn't been activated on any devices."
              size="sm"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Machine ID</TableHead>
                  <TableHead>Activated</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {license.activations.map((activation) => (
                  <TableRow key={activation.activationId}>
                    <TableCell className="font-mono text-gray-500 text-xs">
                      {activation.machineId.substring(0, 12)}...
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {new Date(activation.activatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {new Date(activation.lastSeenAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          activation.status === 'active' ? 'success' : 'error'
                        }
                      >
                        {activation.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default LicenseDetailPage;
